import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  api,
  setTokens,
  setAccessTokenOnly,
  clearTokens,
  getAccessToken,
  isAccessTokenValid,
  restoreSession,
  registerOnTokenRefreshed,
  unregisterOnTokenRefreshed,
} from './api';
import { useToast } from '../components/ui/Toast';
import { checkModuleConsent } from '@inspector/shared';
import type { AuthUserDto, LoginResponse, LoginResult, ModuleKey, SectionPermissionKey } from '@inspector/shared';

/**
 * Sprint hotfix B-040c — kontekst usera dla Sentry.
 *
 * Wywoływane przy każdym ustawieniu/wyczyszczeniu user'a (login, refresh,
 * restore z storage, logout). Pozwala filtrować w Sentry błędy per organizacja
 * / per rola, a stack traces zawierają identyfikatory userów (przy zachowaniu
 * privacy — bez full PII, tylko id + email który Sentry traktuje jako sensitive).
 */
function applySentryUser(user: AuthUserDto | null) {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email, username: `${user.firstName} ${user.lastName}` });
    Sentry.setTag('user.role', user.role);
    Sentry.setTag('organizationId', user.organizationId ?? 'none');
    Sentry.setTag('appVersion', __APP_VERSION__);
  } else {
    Sentry.setUser(null);
  }
}

export type LoginResult2FA =
  | { twoFactorRequired: false }
  | { twoFactorRequired: true; twoFactorToken: string; method?: 'TOTP' | 'EMAIL' };

export interface ImpersonationState {
  orgId: string;
  orgName: string;
}

interface AuthContextType {
  user: AuthUserDto | null;
  loading: boolean;
  impersonation: ImpersonationState | null;
  login: (email: string, password: string, remember?: boolean) => Promise<LoginResult2FA>;
  verifyLogin2fa: (twoFactorToken: string, code: string, remember?: boolean) => Promise<void>;
  resendLogin2fa: (twoFactorToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  impersonate: (orgId: string) => Promise<void>;
  exitImpersonation: () => Promise<void>;
}

// ─── Impersonation storage helpers ───────────────────────────────────────────

const IMPERSONATION_KEY = 'impersonation';
const ORIG_ACCESS_TOKEN_KEY = 'orig_accessToken';

function getStoredImpersonation(): ImpersonationState | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(IMPERSONATION_KEY)
    ?? window.sessionStorage.getItem(IMPERSONATION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
}

function storeImpersonation(state: ImpersonationState, persist: boolean) {
  if (typeof window === 'undefined') return;
  const value = JSON.stringify(state);
  const primary = persist ? window.localStorage : window.sessionStorage;
  const opposite = persist ? window.sessionStorage : window.localStorage;
  primary.setItem(IMPERSONATION_KEY, value);
  opposite.removeItem(IMPERSONATION_KEY);
}

function clearStoredImpersonation() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(IMPERSONATION_KEY);
  window.sessionStorage.removeItem(IMPERSONATION_KEY);
}

function storeOrigAccessToken(token: string, persist: boolean) {
  if (typeof window === 'undefined') return;
  const primary = persist ? window.localStorage : window.sessionStorage;
  const opposite = persist ? window.sessionStorage : window.localStorage;
  primary.setItem(ORIG_ACCESS_TOKEN_KEY, token);
  opposite.removeItem(ORIG_ACCESS_TOKEN_KEY);
}

function getStoredOrigAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(ORIG_ACCESS_TOKEN_KEY)
    ?? window.sessionStorage.getItem(ORIG_ACCESS_TOKEN_KEY);
}

function clearStoredOrigAccessToken() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ORIG_ACCESS_TOKEN_KEY);
  window.sessionStorage.removeItem(ORIG_ACCESS_TOKEN_KEY);
}

const AuthContext = createContext<AuthContextType | null>(null);

function hasWindow(): boolean {
  return typeof window !== 'undefined';
}

function getStoredUser(): AuthUserDto | null {
  if (!hasWindow()) return null;
  const raw = window.localStorage.getItem('user') ?? window.sessionStorage.getItem('user');
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthUserDto;
  } catch {
    window.localStorage.removeItem('user');
    window.sessionStorage.removeItem('user');
    return null;
  }
}

function storeUser(user: AuthUserDto, persist: boolean) {
  if (!hasWindow()) return;
  const value = JSON.stringify(user);
  const primary = persist ? window.localStorage : window.sessionStorage;
  const opposite = persist ? window.sessionStorage : window.localStorage;
  primary.setItem('user', value);
  opposite.removeItem('user');
}

function clearStoredUser() {
  if (!hasWindow()) return;
  window.localStorage.removeItem('user');
  window.sessionStorage.removeItem('user');
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUserDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(
    () => getStoredImpersonation(),
  );
  const navigate = useNavigate();
  const { toast } = useToast();
  // Ref do impersonation — użyte wewnątrz callbacku api.ts bez cyklu zależności
  const impersonationRef = useRef(impersonation);
  impersonationRef.current = impersonation;

  useEffect(() => {
    let active = true;

    async function initializeAuth() {
      const cachedUser = getStoredUser();

      if (isAccessTokenValid()) {
        if (active) {
          setUser(cachedUser);
          applySentryUser(cachedUser);
          setLoading(false);
        }
        // Refresh profile in background to pick up permission/module changes
        try {
          const fresh = await api<AuthUserDto>('/auth/me');
          if (active) {
            setUser(fresh);
            applySentryUser(fresh);
            const persist = typeof window !== 'undefined' && !!window.localStorage.getItem('user');
            storeUser(fresh, persist);
          }
        } catch {
          // Quiet — stale cache is acceptable until next login
        }
        return;
      }

      if (!cachedUser) {
        if (active) {
          clearTokens();
          setLoading(false);
        }
        return;
      }

      const restored = await restoreSession();
      if (!active) {
        return;
      }

      if (restored) {
        setUser(cachedUser);
        applySentryUser(cachedUser);
      } else {
        clearStoredUser();
        setUser(null);
        applySentryUser(null);
      }

      setLoading(false);
    }

    initializeAuth().catch(() => {
      if (!active) {
        return;
      }

      clearTokens();
      clearStoredUser();
      setUser(null);
      applySentryUser(null);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  /**
   * Rejestracja callbacku w api.ts — wywołany po udanym token-refresh.
   * Gdy impersonacja jest aktywna i api.ts odświeży token (przywróci oryginalną sesję),
   * czyścimy stan impersonacji i wyświetlamy toast „Sesja podglądu wygasła".
   * To jest mechanizm auto-exit: token impersonacji wygasł → 401 → api.ts próbuje
   * refresh (ma oryginalny refreshToken) → sukces → callback → impersonation state cleared.
   */
  useEffect(() => {
    registerOnTokenRefreshed(() => {
      if (impersonationRef.current !== null) {
        clearStoredImpersonation();
        clearStoredOrigAccessToken();
        setImpersonation(null);
        toast('Sesja podglądu wygasła — powrót do własnego konta', 'info');
      }
    });
    return () => {
      unregisterOnTokenRefreshed();
    };
  }, [toast]);

  const login = useCallback(async (email: string, password: string, remember = true): Promise<LoginResult2FA> => {
    const data = await api<LoginResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if ('twoFactorRequired' in data) {
      return { twoFactorRequired: true as const, twoFactorToken: data.twoFactorToken, method: data.method };
    }

    setTokens(data.accessToken, data.refreshToken, remember);
    setUser(data.user);
    storeUser(data.user, remember);
    applySentryUser(data.user);
    return { twoFactorRequired: false as const };
  }, []);

  const verifyLogin2fa = useCallback(async (twoFactorToken: string, code: string, remember = true): Promise<void> => {
    const data = await api<LoginResponse>('/auth/login/2fa', {
      method: 'POST',
      body: JSON.stringify({ twoFactorToken, code }),
    });

    setTokens(data.accessToken, data.refreshToken, remember);
    setUser(data.user);
    storeUser(data.user, remember);
    applySentryUser(data.user);
  }, []);

  const resendLogin2fa = useCallback(async (twoFactorToken: string): Promise<void> => {
    await api('/auth/login/2fa/resend', {
      method: 'POST',
      body: JSON.stringify({ twoFactorToken }),
    });
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const profile = await api<AuthUserDto>('/auth/me');
      setUser(profile);
      applySentryUser(profile);
      // Preserve storage mode (localStorage vs sessionStorage).
      const persist = typeof window !== 'undefined' && !!window.localStorage.getItem('user');
      storeUser(profile, persist);
    } catch {
      // Quiet: /auth/me failing means token expired, ProtectedRoute handles redirect.
    }
  }, []);

  /**
   * Wejdź w kontekst organizacji jako ORG_ADMIN (impersonacja read-only).
   * Token-swap: oryginalny accessToken zapisany jako orig_accessToken,
   * token impersonacji ustawiany jako aktywny accessToken (bez nadpisywania refreshToken).
   */
  const impersonate = useCallback(async (orgId: string) => {
    const data = await api<{ token: string; organization: { id: string; name: string } }>(
      `/platform/impersonate/${orgId}`,
      { method: 'POST' },
    );

    // Persist mode: czy user ma dane w localStorage?
    const persist = typeof window !== 'undefined' && !!window.localStorage.getItem('user');

    // Zachowaj oryginalny accessToken
    const origToken = getAccessToken();
    if (origToken) {
      storeOrigAccessToken(origToken, persist);
    }

    // Ustaw token impersonacji jako aktywny (NIE nadpisuj refreshToken)
    setAccessTokenOnly(data.token);

    // Zapisz stan impersonacji
    const impersonationState: ImpersonationState = {
      orgId: data.organization.id,
      orgName: data.organization.name,
    };
    storeImpersonation(impersonationState, persist);
    setImpersonation(impersonationState);

    // Pobierz efektywny profil (ORG_ADMIN org X)
    await refreshProfile();
    navigate('/dashboard');
  }, [navigate, refreshProfile]);

  /**
   * Wyjdź z impersonacji — przywróć oryginalny token i sesję.
   */
  const exitImpersonation = useCallback(async () => {
    // Powiadom backend (ignore błąd — możliwe że token już wygasł)
    try {
      await api('/platform/impersonate/stop', { method: 'POST' });
    } catch {
      // ignore
    }

    // Przywróć oryginalny accessToken
    const origToken = getStoredOrigAccessToken();
    if (origToken) {
      setAccessTokenOnly(origToken);
    }

    // Wyczyść impersonation state
    clearStoredOrigAccessToken();
    clearStoredImpersonation();
    setImpersonation(null);

    // Odśwież profil (powrót do SUPER_ADMIN)
    await refreshProfile();
    navigate('/admin/organizations');
  }, [navigate, refreshProfile]);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // ignore
    }
    clearTokens();
    clearStoredUser();
    clearStoredImpersonation();
    clearStoredOrigAccessToken();
    setImpersonation(null);
    setUser(null);
    applySentryUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, impersonation, login, verifyLogin2fa, resendLogin2fa, logout, refreshProfile, impersonate, exitImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/**
 * Feature flags per organizacja — Sprint 40.
 * Zwraca true dla SUPER_ADMIN niezależnie od flag (kompatybilne z backendem).
 */
export function useModuleEnabled(key: ModuleKey): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  return user.enabledModules?.[key] === true;
}

/**
 * Zgoda organizacji na moduł — **bez obejścia rolą**.
 *
 * `useModuleEnabled` przepuszcza SUPER_ADMINa niezależnie od flagi i tak ma
 * zostać: dla modułów czysto funkcjonalnych (np. `cad`) to wygodny pełny widok
 * ponad organizacjami. Ale flaga `aiPhotoAnalysis` nie jest przełącznikiem
 * funkcji — jest **zgodą na wysłanie zdjęć klienta do zewnętrznego dostawcy**
 * (spec §10). Zgoda, którą obchodzi rola, nie jest zgodą, więc interfejs nie
 * może oferować wysłania tam, gdzie backend i tak odmówi: `assertConsent`
 * w `apps/api/src/ai/ai-proposal.service.ts` czyta `enabledModules` wprost,
 * z pominięciem `ModuleAccessService`, właśnie z tego powodu.
 *
 * Ten hook jest frontowym odpowiednikiem tamtej reguły.
 */
export function useModuleConsent(key: ModuleKey): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return checkModuleConsent(user.enabledModules, key);
}

export function useModuleEnabledForProject(
  key: ModuleKey,
  projectModules?: Record<string, boolean> | null,
): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN') return true;
  if (user.enabledModules?.[key] !== true) return false;
  if (projectModules && projectModules[key] === false) return false;
  return true;
}

export function useHasSectionPermission(key: SectionPermissionKey): boolean {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === 'SUPER_ADMIN' || user.role === 'ORG_ADMIN') return true;
  return user.permissions?.[key] === true;
}
